import { CreateVariableBulkDto } from "@/components/CreateVariablesBulk";

export interface CsvValidationError {
  row: number;
  column: string;
  value: string;
  error: string;
  severity: "error" | "warning";
}

export interface CsvValidationResult {
  isValid: boolean;
  data: CreateVariableBulkDto[];
  errors: CsvValidationError[];
  warnings: string[];
  stats: {
    totalRows: number;
    validRows: number;
    errorRows: number;
    duplicateCombinations: string[]; // Changed from duplicateNames to duplicateCombinations
    emptyRows: number;
  };
}

export const REQUIRED_HEADERS = [
  "variableName",
  "scadaReferenceName",
  "zoneName",
  "tableName",
] as const;
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export const MAX_FIELD_LENGTH = 100;
export const MAX_ROWS = 1000;

// CSV separator options
export type CsvSeparator = "," | ";";
export const CSV_SEPARATORS = {
  COMMA: "," as CsvSeparator,
  SEMICOLON: ";" as CsvSeparator,
} as const;

// Regular expressions for validation
// Only restrict characters that are truly problematic for file systems and databases
const INVALID_CHARS_REGEX = /[<>:"\\|?*\x00-\x1f]/; // Removed common industrial characters like (), _, -, /, etc.
const WHITESPACE_ONLY_REGEX = /^\s*$/;

/**
 * Validates if a file is a valid CSV file
 */
export const validateFile = (
  file: File,
): { isValid: boolean; error?: string } => {
  if (!file) {
    return { isValid: false, error: "No se ha seleccionado ningún archivo" };
  }

  if (!file.name.toLowerCase().endsWith(".csv")) {
    return { isValid: false, error: "El archivo debe tener extensión .csv" };
  }

  if (file.size === 0) {
    return { isValid: false, error: "El archivo está vacío" };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `El archivo es demasiado grande. Máximo ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(1)}MB permitido.`,
    };
  }

  return { isValid: true };
};

/**
 * Detects the CSV separator used in a line
 */
export const detectCsvSeparator = (line: string): CsvSeparator => {
  const commaCount = (line.match(/,/g) || []).length;
  const semicolonCount = (line.match(/;/g) || []).length;

  // If both exist, choose the one with more occurrences
  if (semicolonCount > commaCount) {
    return CSV_SEPARATORS.SEMICOLON;
  }
  return CSV_SEPARATORS.COMMA;
};

/**
 * Parses a CSV line respecting quoted fields with specified separator
 */
export const parseCsvLine = (
  line: string,
  separator: CsvSeparator = ",",
): string[] => {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote within quoted field
        current += '"';
        i += 2;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
        i++;
      }
    } else if (char === separator && !inQuotes) {
      // Field separator
      result.push(current.trim());
      current = "";
      i++;
    } else {
      current += char;
      i++;
    }
  }

  // Add the last field
  result.push(current.trim());

  return result;
};

/**
 * Validates a single field value
 */
export const validateField = (
  value: string,
  fieldName: string,
  isRequired: boolean = true,
): CsvValidationError[] => {
  const errors: CsvValidationError[] = [];

  // Check if required field is empty
  if (isRequired && (!value || WHITESPACE_ONLY_REGEX.test(value))) {
    errors.push({
      row: 0,
      column: fieldName,
      value: value || "",
      error: `El campo ${fieldName} es requerido`,
      severity: "error",
    });
    return errors; // Return early if required field is missing
  }

  // Skip further validation if field is empty but not required
  if (!value || WHITESPACE_ONLY_REGEX.test(value)) {
    return errors;
  }

  // Check field length
  if (value.length > MAX_FIELD_LENGTH) {
    errors.push({
      row: 0,
      column: fieldName,
      value,
      error: `El campo ${fieldName} no puede exceder ${MAX_FIELD_LENGTH} caracteres`,
      severity: "error",
    });
  }

  // Check for invalid characters (only truly problematic ones)
  if (INVALID_CHARS_REGEX.test(value)) {
    errors.push({
      row: 0,
      column: fieldName,
      value,
      error: `El campo ${fieldName} contiene caracteres no válidos (< > : " / \\ | ? *)`,
      severity: "error",
    });
  }

  // Check for potential encoding issues (replacement characters)
  if (value.includes("�")) {
    errors.push({
      row: 0,
      column: fieldName,
      value,
      error: `El campo ${fieldName} parece tener problemas de codificación. Guarde el archivo como UTF-8`,
      severity: "warning",
    });
  }

  // Check for leading/trailing whitespace
  if (value !== value.trim()) {
    errors.push({
      row: 0,
      column: fieldName,
      value,
      error: `El campo ${fieldName} contiene espacios al inicio o final`,
      severity: "warning",
    });
  }

  return errors;
};

/**
 * Validates the CSV structure and content with separator detection
 */
export const validateCsvContent = (
  csvText: string,
  forceSeparator?: CsvSeparator,
): CsvValidationResult => {
  const lines = csvText
    .trim()
    .split("\n")
    .filter((line) => line.trim()); // Remove empty lines
  const errors: CsvValidationError[] = [];
  const warnings: string[] = [];
  const validData: CreateVariableBulkDto[] = [];
  const processedCombinations = new Set<string>(); // Changed from processedNames to processedCombinations
  const duplicateCombinations: string[] = [];
  let emptyRows = 0;

  // Check if file has content
  if (lines.length === 0) {
    return {
      isValid: false,
      data: [],
      errors: [
        {
          row: 0,
          column: "file",
          value: "",
          error: "El archivo está vacío o no contiene datos válidos",
          severity: "error",
        },
      ],
      warnings: [],
      stats: {
        totalRows: 0,
        validRows: 0,
        errorRows: 0,
        duplicateCombinations: [],
        emptyRows: 0,
      },
    };
  }
  // Parse and validate headers
  const headerLine = lines[0];
  const separator = forceSeparator || detectCsvSeparator(headerLine);
  const headers = parseCsvLine(headerLine, separator).map((h) => h.trim());

  // Check for required headers
  const missingHeaders = REQUIRED_HEADERS.filter(
    (required) => !headers.includes(required),
  );

  if (missingHeaders.length > 0) {
    return {
      isValid: false,
      data: [],
      errors: [
        {
          row: 1,
          column: "headers",
          value: headers.join(", "),
          error: `Faltan columnas requeridas: ${missingHeaders.join(", ")}`,
          severity: "error",
        },
      ],
      warnings: [],
      stats: {
        totalRows: lines.length - 1,
        validRows: 0,
        errorRows: 0,
        duplicateCombinations: [],
        emptyRows: 0,
      },
    };
  }

  // Check for extra headers
  const extraHeaders = headers.filter(
    (header) =>
      !REQUIRED_HEADERS.includes(header as any) && header.trim() !== "",
  );

  if (extraHeaders.length > 0) {
    warnings.push(
      `Se encontraron columnas adicionales que serán ignoradas: ${extraHeaders.join(", ")}`,
    );
  }

  // Get column indices
  const getColumnIndex = (columnName: string) => headers.indexOf(columnName);
  const columnIndices = {
    variableName: getColumnIndex("variableName"),
    scadaReferenceName: getColumnIndex("scadaReferenceName"),
    zoneName: getColumnIndex("zoneName"),
    tableName: getColumnIndex("tableName"),
  };

  // Validate data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    const rowNumber = i + 1;

    // Skip empty lines
    if (!line || WHITESPACE_ONLY_REGEX.test(line)) {
      emptyRows++;
      continue;
    }
    const values = parseCsvLine(line, separator);
    let hasRowErrors = false;

    // Check if row has correct number of columns
    if (values.length < headers.length) {
      errors.push({
        row: rowNumber,
        column: "structure",
        value: line,
        error: `La fila tiene ${values.length} columnas, se esperaban al menos ${headers.length}`,
        severity: "error",
      });
      hasRowErrors = true;
      continue;
    }

    // Extract field values
    const fields = {
      variableName: values[columnIndices.variableName]?.trim() || "",
      scadaReferenceName:
        values[columnIndices.scadaReferenceName]?.trim() || "",
      zoneName: values[columnIndices.zoneName]?.trim() || "",
      tableName: values[columnIndices.tableName]?.trim() || "",
    };

    // Validate each field
    Object.entries(fields).forEach(([fieldName, value]) => {
      const fieldErrors = validateField(value, fieldName, true);
      fieldErrors.forEach((error) => {
        errors.push({
          ...error,
          row: rowNumber,
        });
        if (error.severity === "error") {
          hasRowErrors = true;
        }
      });
    }); // Check for duplicate combinations (variableName + zoneName + tableName must be unique)
    if (fields.variableName && fields.zoneName && fields.tableName) {
      const combinationKey = `${fields.variableName.toLowerCase()}|${fields.zoneName.toLowerCase()}|${fields.tableName.toLowerCase()}`;
      if (processedCombinations.has(combinationKey)) {
        const duplicateIdentifier = `${fields.variableName} (${fields.zoneName} - ${fields.tableName})`;
        duplicateCombinations.push(duplicateIdentifier);
        errors.push({
          row: rowNumber,
          column: "combination",
          value: duplicateIdentifier,
          error:
            "Combinación de variable, zona y tabla duplicada en el archivo",
          severity: "error",
        });
        hasRowErrors = true;
      } else {
        processedCombinations.add(combinationKey);
      }
    }

    // If no errors, add to valid data
    if (!hasRowErrors) {
      validData.push({
        variableName: fields.variableName,
        scadaReferenceName: fields.scadaReferenceName,
        zoneName: fields.zoneName,
        tableName: fields.tableName,
      });
    }
  }

  // Add general warnings
  if (validData.length === 0 && errors.length === 0) {
    warnings.push("No se encontraron datos válidos para procesar");
  }

  if (validData.length > MAX_ROWS) {
    warnings.push(
      `Se detectaron más de ${MAX_ROWS} variables. Considere dividir en archivos más pequeños para mejor rendimiento.`,
    );
  }

  if (emptyRows > 0) {
    warnings.push(`Se ignoraron ${emptyRows} filas vacías`);
  }
  // Calculate statistics
  const errorRows = [
    ...new Set(
      errors
        .filter((e) => e.row > 1 && e.severity === "error")
        .map((e) => e.row),
    ),
  ].length;

  const stats = {
    totalRows: lines.length - 1,
    validRows: validData.length,
    errorRows,
    duplicateCombinations: [...new Set(duplicateCombinations)],
    emptyRows,
  };

  return {
    isValid:
      errors.filter((e) => e.severity === "error").length === 0 &&
      validData.length > 0,
    data: validData,
    errors,
    warnings,
    stats,
  };
};

/**
 * Generates a sample CSV content for download with specified separator
 */
export const generateSampleCsv = (separator: CsvSeparator = ","): string => {
  const headers = REQUIRED_HEADERS.join(separator);
  const sampleData = [
    [
      "Variable_Temperatura_Sala_1",
      "TEMP_001",
      "Zona Producción",
      "Tabla Sensores",
    ].join(separator),
    [
      "Variable_Presion_Linea_A",
      "PRES_002",
      "Zona Proceso",
      "Tabla Actuadores",
    ].join(separator),
    [
      "Variable_Caudal_Entrada",
      "FLOW_003",
      "Zona Entrada",
      "Tabla Medidores",
    ].join(separator),
    [
      "Variable_Velocidad_Motor",
      "VEL_004",
      "Zona Motores",
      "Tabla Control",
    ].join(separator),
    [
      "Variable_Nivel_Tanque",
      "NIV_005",
      "Zona Almacenamiento",
      "Tabla Niveles",
    ].join(separator),
  ];

  return [headers, ...sampleData].join("\n");
};

/**
 * Downloads a sample CSV file with specified separator
 */
export const downloadSampleCsv = (
  separator: CsvSeparator = ",",
  locale: "es" | "en" = "es",
): void => {
  const content = generateSampleCsv(separator);
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const filename = `plantilla_variables_${separator === ";" ? "es" : "en"}.csv`;
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }
};
